// SignupStep2.jsx — Email & Mobile Number
import React, { useState, useEffect } from "react";
// adjust the relative path if this file lives deeper/shallower than utils/
import {
  COUNTRY_CODES,
  findCountryByIso,
  pickCountryIso,
  parsePhoneInput,
  isPlausibleNationalNumber,
} from "../../utils/countryCodes";

// ------------------------------------------------------------
// Validation helpers
// ------------------------------------------------------------

const EMAIL_REGEX =
  /^[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

function isValidEmail(email) {
  const value = (email || "").trim();
  if (!value || value.length > 254) return false;
  if (value.includes("..")) return false; // no consecutive dots
  if (value.startsWith(".") || value.split("@")[0].endsWith(".")) return false;
  return EMAIL_REGEX.test(value);
}

function getEmailError(email) {
  const value = (email || "").trim();
  if (!value) return "Email is required.";
  if (value.length > 254) return "Email is too long.";
  if (!isValidEmail(value)) return "Please enter a valid email address.";
  return "";
}

// Only digits matter for validation (spaces/dashes/brackets are allowed in
// the box but ignored here).
function digitsOnly(value) {
  return String(value || "").replace(/\D/g, "");
}

function getPhoneError(phone, dialCode) {
  if (!dialCode) return "Please select a country code.";

  const digits = digitsOnly(phone);
  if (!digits) return "Mobile number is required.";

  // India: exactly 10 digits, starting with 6-9
  if (digitsOnly(dialCode) === "91") {
    if (digits.length !== 10) {
      return "Indian mobile numbers must be exactly 10 digits.";
    }
    if (!/^[6-9]/.test(digits)) {
      return "Indian mobile numbers must start with 6, 7, 8 or 9.";
    }
    return "";
  }

  if (!isPlausibleNationalNumber(phone)) {
    return "Please enter a valid mobile number.";
  }
  return "";
}

// ------------------------------------------------------------
// Component
// ------------------------------------------------------------

const Step2 = ({ formData, setFormData, nextStep, prevStep }) => {
  // Which country row is selected. Local state (not re-derived from the dial
  // code on every render) because many countries share a code — +1 is the US,
  // Canada, Jamaica... — and re-deriving would snap "Canada" back to "United
  // States" as soon as the form re-rendered.
  const [selectedIso, setSelectedIso] = useState(() =>
    pickCountryIso(formData.phoneCountryCode, formData.country)
  );

  // Show an error only after the user has touched the field (or tried to
  // continue), so the form isn't red before they start typing.
  const [touched, setTouched] = useState({ email: false, phone: false });

  // If the country was already chosen earlier in signup, start on its code —
  // and save it into the form, so what the user sees selected is what is
  // actually submitted.
  useEffect(() => {
    if (!formData.phoneCountryCode && selectedIso) {
      const country = findCountryByIso(selectedIso);
      if (country) setFormData({ ...formData, phoneCountryCode: country.dial });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markTouched = (field) =>
    setTouched((prev) => ({ ...prev, [field]: true }));

  const handleEmailChange = (e) => {
    // No spaces allowed in an email address
    const value = e.target.value.replace(/\s/g, "");
    setFormData({ ...formData, email: value });
  };

  // Trim + lowercase once the user leaves the field
  const handleEmailBlur = () => {
    setFormData({ ...formData, email: (formData.email || "").trim().toLowerCase() });
    markTouched("email");
  };

  const handleCodeChange = (e) => {
    const iso = e.target.value;
    setSelectedIso(iso);
    const country = findCountryByIso(iso);
    setFormData({ ...formData, phoneCountryCode: country ? country.dial : "" });
  };

  // Digits only (plus spaces/dashes/brackets). A pasted international number
  // ("+91 98765 43210") is split: the code moves to the dropdown, the rest
  // stays in the box.
  const handlePhoneChange = (e) => {
    const parsed = parsePhoneInput(e.target.value, formData.country);
    const next = { ...formData, phone: parsed.phone };
    if (parsed.dial) {
      next.phoneCountryCode = parsed.dial;
      setSelectedIso(parsed.iso);
    }
    setFormData(next);
  };

  const emailError = getEmailError(formData.email);
  const phoneError = getPhoneError(formData.phone, formData.phoneCountryCode);

  const canContinue = !emailError && !phoneError;

  const showEmailError = touched.email && !!emailError;
  const showPhoneError = touched.phone && !!phoneError;

  const handleContinue = () => {
    // Reveal any errors, and stop if the form isn't valid
    setTouched({ email: true, phone: true });
    if (!canContinue) return;

    // Save clean values before moving on
    setFormData({
      ...formData,
      email: (formData.email || "").trim().toLowerCase(),
      phone: digitsOnly(formData.phone),
    });
    nextStep();
  };

  return (
    <div className="step2">
      <div className="left-icon1">
        <button type="button" className="backbutton" onClick={prevStep}>
          <i className="fa fa-long-arrow-left" aria-hidden="true"></i>
        </button>
      </div>
      <div className="icon-blk text-center">
        <img src="images/profile-icon.svg" alt="Profile" />
      </div>
      <div className="form-basic">
        <h5 className="modal-title">Email ID</h5>
        <div className="mb-3">
          <input
            type="email"
            className={`form-control ${showEmailError ? "is-invalid" : ""}`}
            name="email"
            value={formData.email || ""}
            onChange={handleEmailChange}
            onBlur={handleEmailBlur}
            placeholder="Email ID"
            autoComplete="email"
            maxLength={254}
            aria-invalid={showEmailError}
            aria-describedby={showEmailError ? "email-error" : undefined}
          />
          {showEmailError && (
            <div id="email-error" className="text-danger small mt-1" role="alert">
              {emailError}
            </div>
          )}
        </div>

        <h5 className="modal-title">Mobile no.</h5>
        <div className="mb-3">
          <div style={{ display: "flex", gap: 8 }}>
            <select
              aria-label="Country code"
              name="phoneCountryCode"
              className="form-select"
              value={selectedIso}
              onChange={handleCodeChange}
              onBlur={() => markTouched("phone")}
              style={{ flex: "0 0 42%", minWidth: 0 }}
            >
              {!selectedIso && <option value="">Code</option>}
              {COUNTRY_CODES.map((c) => (
                <option key={c.iso} value={c.iso}>
                  {c.name} ({c.dial})
                </option>
              ))}
            </select>
            <input
              type="tel"
              inputMode="tel"
              className={`form-control ${showPhoneError ? "is-invalid" : ""}`}
              name="phone"
              value={formData.phone || ""}
              onChange={handlePhoneChange}
              onBlur={() => markTouched("phone")}
              placeholder="Mobile number"
              autoComplete="tel-national"
              maxLength={20}
              aria-invalid={showPhoneError}
              aria-describedby={showPhoneError ? "phone-error" : undefined}
              style={{ flex: 1, minWidth: 0 }}
            />
          </div>
          {showPhoneError && (
            <div id="phone-error" className="text-danger small mt-1" role="alert">
              {phoneError}
            </div>
          )}
        </div>

        <div className="modal-footer border-0 justify-content-center p-0 footer-modal mt-4">
          <button
            type="button"
            className="py-2 btn-filled"
            onClick={handleContinue}
            disabled={!canContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default Step2;